// Tiny API client. All requests include the Cognito ID token as Bearer auth.

import { getStoredTokens, startSignIn } from './auth';

export interface Persona {
  id: string;
  role: 'customer' | 'subcontractor' | 'internal';
  displayName: string;
  emailLocalPart: string;
  phone?: string;
  unitNumber?: string;
  category?: string;
  // If set, persona is only valid for these recipients (Compose UI filters by
  // the chosen To address; Lambda backstops). Unset/empty = universal.
  allowedRecipients?: string[];
  createdAt: string;
}

export interface SentMessage {
  id: string;
  sentAt: string;
  fromPersonaId: string;
  fromAddress: string;
  fromDisplayName: string;
  recipient: string;
  subject: string;
  body: string;
  messageId: string;
  inReplyToMessageId?: string;
  references?: string;
}

export interface ReceivedMessage {
  id: string;
  receivedAt: string;
  fromAddress: string;
  fromDisplayName: string;
  toAddresses: string[];
  matchedPersonaIds: string[];
  subject: string;
  body: string;
  messageId: string;
  inReplyToMessageId?: string;
  references?: string;
  rawS3Key: string;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const tokens = getStoredTokens();
  if (!tokens) {
    await startSignIn(window.location.pathname);
    throw new Error('Redirecting to sign-in');
  }
  const res = await fetch(path, {
    method,
    headers: {
      authorization: `Bearer ${tokens.id_token}`,
      ...(body ? { 'content-type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    // Token may be expired even though we checked exp; redirect to sign-in.
    localStorage.removeItem('mail-sim:tokens');
    await startSignIn(window.location.pathname);
    throw new Error('Session expired');
  }
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    throw new Error(`Unexpected response: ${res.status} ${await res.text()}`);
  }
  const json = await res.json();
  if (!res.ok) {
    throw new Error(json.message ?? `${method} ${path} failed: ${res.status}`);
  }
  return json as T;
}

// Config (allowed recipients for the Compose UI)
export const getConfig = () =>
  request<{ recipients: string[]; defaultRecipient: string }>('GET', '/api/config');

// Personas
export const listPersonas = () => request<{ personas: Persona[] }>('GET', '/api/personas');
export const createPersona = (input: Omit<Persona, 'id' | 'createdAt'>) =>
  request<{ persona: Persona }>('POST', '/api/personas', input);
export const deletePersona = (id: string) =>
  request<{ deleted: string }>('DELETE', `/api/personas/${encodeURIComponent(id)}`);

// Messages (sent)
export const listMessages = () => request<{ messages: SentMessage[] }>('GET', '/api/messages');
export const getMessage = (id: string) =>
  request<{ message: SentMessage }>('GET', `/api/messages/${encodeURIComponent(id)}`);
export const sendMessage = (input: {
  fromPersonaId: string;
  subject: string;
  body: string;
  to?: string;
  inReplyToId?: string;
  // Reply-to-inbox path: ID of a ReceivedMessage. Mutually exclusive with inReplyToId.
  inboundParentId?: string;
}) => request<{ message: SentMessage }>('POST', '/api/messages/send', input);

// Inbox (received)
export const listInbox = () => request<{ messages: ReceivedMessage[] }>('GET', '/api/inbox');
export const getInboxMessage = (id: string) =>
  request<{ message: ReceivedMessage }>('GET', `/api/inbox/${encodeURIComponent(id)}`);
